import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import { useScreenSize } from 'shared/lib/screen/useScreenSize';
import { StorageKeys } from 'shared/lib/storage/storageService';

export const SIDEBAR_WIDTH = 216;
export const SIDEBAR_RAIL_WIDTH = 56;

export const SIDEBAR_WIDTH_VAR = '--sidebar-width';
export const SIDEBAR_TRANSITION = { duration: 0.2, ease: 'easeOut' } as const;

type SidebarState = {
  isPinned: boolean | null;
  isHovered: boolean;
  /**
   * Whether something else on screen has claimed the width the expanded sidebar wants.
   *
   * Separate from `isPinned` so the user's own choice survives: the sidebar gives up the space while
   * the claim stands and goes back to whatever they pinned once it is released. Set by whoever is
   * doing the claiming — `shared` can hold the flag but must not know who reads or writes it.
   */
  isSuppressed: boolean;
  setPinned: (isPinned: boolean) => void;
  setHovered: (isHovered: boolean) => void;
  setSuppressed: (isSuppressed: boolean) => void;
};

export const useSidebarStore = create<SidebarState>()(
  persist(
    (set) => ({
      isPinned: null,
      isHovered: false,
      isSuppressed: false,
      setPinned: (isPinned) => set({ isPinned }),
      setHovered: (isHovered) => set({ isHovered }),
      setSuppressed: (isSuppressed) => set({ isSuppressed }),
    }),
    {
      name: StorageKeys.SIDEBAR_STATE,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ isPinned: state.isPinned }),
    },
  ),
);

export const useIsSidebarPinned = () => {
  const isPinned = useSidebarStore((state) => state.isPinned);
  const isSuppressed = useSidebarStore((state) => state.isSuppressed);
  const { isWideDesktop } = useScreenSize();

  return !isSuppressed && (isPinned ?? isWideDesktop);
};

export const useIsSidebarExpanded = () => {
  const isHovered = useSidebarStore((state) => state.isHovered);

  return useIsSidebarPinned() || isHovered;
};

/** Space the sidebar takes in the layout. Hover-expanding widens it, so the content moves with it. */
export const useSidebarWidth = () => (useIsSidebarExpanded() ? SIDEBAR_WIDTH : SIDEBAR_RAIL_WIDTH);
